import React from 'react';
import { BrowserRouter as Router, Route, Routes } from 'react-router-dom';
import { AuthProvider } from './context/AuthContext';
import LoginPage from './components/Login/LoginPage';
import Dashboard from './components/Dashboard/DashboardPage';
import ProtectedRoute from './context/ProtectedRoute';
import Users from './components/User/UserPage';
import EditUser from './components/User/EditUser';
import { ThemeProvider, createTheme } from "@mui/material";
import Roles from './components/Roles/RolePage';

// import { appInsights } from './AppInsights';
import ForgotPassword from './components/ForgotPassword/ForgotPassword';
import ForgotPasswordSuccess from './components/ForgotPassword/ForgotPasswordSuccess';
import ResetPassword from './components/ForgotPassword/ResetPassword';
import ResetPasswordSuccess from './components/ForgotPassword/ResetPasswordSuccess';
import PageNotFound from './components/PageNotFound';
import NewPassword from './components/NewPassword/NewPassword';
import NewPasswordSuccess from './components/NewPassword/NewPasswordSuccess';

import Webhook from './components/Webhooks/WebhookPage';
import Brand from './components/Brands/BrandPage';
import Group from './components/Groups/GroupPage';
import Api from './components/API/ApiPage';
import Clients from './components/Clients/ClientPage';
import EditClient from './components/Clients/EditClient';
import Template from './components/Templates/TemplatePage';
import AddTemplateForm from './components/Templates/AddTemplateForm';
import EditTemplateForm from './components/Templates/EditTemplateForm';
import NewCampaing from './components/Campaigns/NewCampaing';
import Campaigns from './components/Campaigns/CampaignPage';
import EditCampaing from './components/Campaigns/EditCampaing';
import Trigger from './components/Triggers/TriggerPage';
import AddTriggerForm from './components/Triggers/AddTriggerForm';
import EditTriggerForm from './components/Triggers/EditTriggerForm';
import AddClient from './components/Clients/AddClient';
import EmailAPIClients from './components/EmailAPI/Clients/ClientPage';

import AddEmailAPIClient from './components/EmailAPI/Clients/AddClient';
import EditEmailAPIClient from './components/EmailAPI/Clients/EditClient';

import ESPSettings from './components/EmailAPI/ESPConnection/ESPPage';
import AddESPSetting from './components/EmailAPI/ESPConnection/AddESP';
import EditESPAPI from './components/EmailAPI/ESPConnection/EditESP';

import EditAppConfig from './components/AppConfig/EditAppConfig';
// import ViewTriggerReports from './components/Triggers/ViewTriggerReports';
import ViewCampaignReports from './components/Campaigns/ViewCampaignReports';
import CampaignSummary from './components/Campaigns/CampaignSummary';
import BillingReport from './components/Reports/BillingReports';
import TestEmailsendReport from './components/Reports/TestEmailsendReports';
import BounceReport from './components/Reports/BounceReport';
import SeedList from './components/SeedList/SeedList';
import SuppressionList from './components/SuppressionList/SuppressionList';
import BulkEmail from './components/BulkEmail/BulkEmailPage';
import NewBulkCampaign from './components/BulkEmail/NewBulkCampaign';
import EditBulkEmailCampaign from './components/BulkEmail/EditBulkEmailCampaign';
import GanttSchedulePage from './components/BulkEmail/GanttChart/GanttSchedulePage';
import Unsubscribe from './components/Unsubscribe/UnsubscribePage'

const theme = createTheme({
  palette: {
    primary: {
      main: getComputedStyle(document.documentElement).getPropertyValue("--primary-color").trim() !== "" 
        ? getComputedStyle(document.documentElement).getPropertyValue("--primary-color").trim() 
        : "#d6242d",
    },
    text: {
      primary: getComputedStyle(document.documentElement).getPropertyValue("--font-color").trim() !== "" 
        ? getComputedStyle(document.documentElement).getPropertyValue("--font-color").trim() 
        : "#3A3A3A",
    },
    divider: getComputedStyle(document.documentElement).getPropertyValue("--border-color").trim() !== "" 
      ? getComputedStyle(document.documentElement).getPropertyValue("--border-color").trim() 
      : "#E6ECF2",
  },
  typography: {
    fontFamily: "Manrope, 'sans-serif'",
    fontSize: isNaN(parseInt(getComputedStyle(document.documentElement).getPropertyValue("--primary-font-size"))) 
      ? 14 
      : parseInt(getComputedStyle(document.documentElement).getPropertyValue("--primary-font-size")),
    allVariants: {
      color: getComputedStyle(document.documentElement).getPropertyValue("--font-color").trim() !== "" 
        ? getComputedStyle(document.documentElement).getPropertyValue("--font-color").trim() 
        : "#3A3A3A",
    },
  },
  components: {
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          "& fieldset": {
            borderColor: getComputedStyle(document.documentElement).getPropertyValue("--border-color").trim() !== "" 
              ? getComputedStyle(document.documentElement).getPropertyValue("--border-color").trim() 
              : "#E6ECF2",
          },
          "&:hover fieldset": {
            borderColor: getComputedStyle(document.documentElement).getPropertyValue("--border-color").trim() !== "" 
            ? getComputedStyle(document.documentElement).getPropertyValue("--border-color").trim() 
            : "#E6ECF2",
          },
          "& .MuiOutlinedInput-notchedOutline": {
            borderColor: getComputedStyle(document.documentElement).getPropertyValue("--border-color").trim() !== "" 
            ? getComputedStyle(document.documentElement).getPropertyValue("--border-color").trim() 
            : "#E6ECF2",
          },
          "&:hover .MuiOutlinedInput-notchedOutline": {
            borderColor: getComputedStyle(document.documentElement).getPropertyValue("--border-color").trim() !== "" 
            ? getComputedStyle(document.documentElement).getPropertyValue("--border-color").trim() 
            : "#E6ECF2",
          },
          "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
            borderColor: getComputedStyle(document.documentElement).getPropertyValue("--border-color").trim() !== "" 
            ? getComputedStyle(document.documentElement).getPropertyValue("--border-color").trim() 
            : "#E6ECF2",
            borderWidth: '1px'
          },
          "& .MuiOutlinedInput-notchedOutline legend": {
            color: getComputedStyle(document.documentElement).getPropertyValue("--border-color").trim() !== "" 
            ? getComputedStyle(document.documentElement).getPropertyValue("--border-color").trim() 
            : "#E6ECF2",
          },
        },
      },
    },
  },
})

const AppRoutes: React.FC = () => {
  // const location = useLocation();

  // useEffect(() => {
  //   appInsights.trackPageView({ name: location.pathname });
  // }, [location]);

  return (
    <Routes>
      <Route path="/" element={<LoginPage />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />

      <Route path="/forgot-password-success" element={<ForgotPasswordSuccess />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/reset-password-success" element={<ResetPasswordSuccess />} />
      <Route path="/create-password" element={<NewPassword />} />
      <Route path="/create-password-success" element={<NewPasswordSuccess />} />
      <Route path="/404" element={<PageNotFound />} />
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            <Dashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/template"
        element={
          <ProtectedRoute>
            <Template />
          </ProtectedRoute>
        }
      />
      <Route
        path="/template/new-template"
        element={
          <ProtectedRoute>
            <AddTemplateForm />
          </ProtectedRoute>
        }
      />
      <Route
        path="/template/edit-template/edit/:id"
        element={
          <ProtectedRoute>
            <EditTemplateForm />
          </ProtectedRoute>
        }
      />
      <Route
        path="/brandsandgroups/manage-brands"
        element={
          <ProtectedRoute>
            <Brand />
          </ProtectedRoute>
        }
      />
      <Route
        path="/brandsandgroups/manage-groups"
        element={
          <ProtectedRoute>
            <Group />
          </ProtectedRoute>
        }
      />
      <Route
        path="/apiandwebhooks/manage-api"
        element={
          <ProtectedRoute>
            <Api />
          </ProtectedRoute>
        }
      />
      <Route
        path="/apiandwebhooks/manage-webhooks"
        element={
          <ProtectedRoute>
            <Webhook />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/manage-users"
        element={
          <ProtectedRoute>
            <Users />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/manage-roles"
        element={
          <ProtectedRoute>
            <Roles />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/manage-users/edit/:id"
        element={
          <ProtectedRoute>
            <EditUser />
          </ProtectedRoute>
        }
      />

      <Route
        path="/admin/manage-clients"
        element={
          <ProtectedRoute>
            <Clients />
          </ProtectedRoute>
        }
      />

      <Route
        path="/admin/manage-clients/add-client/:id?"
        element={
          <ProtectedRoute>
            <AddClient />
          </ProtectedRoute>
        }
      />

      <Route
        path="/admin/manage-clients/edit/:id"
        element={
          <ProtectedRoute>
            <EditClient />
          </ProtectedRoute>
        }
      />

      <Route
        path="/admin/manage-users/edit/:id"
        element={
          <ProtectedRoute>
            <EditUser />
          </ProtectedRoute>
        }
      />

      <Route
        path="/admin/manage-appconfig"
        element={
          <ProtectedRoute>
            <EditAppConfig />
          </ProtectedRoute>
        }
      />

      <Route
        path="/campaigns"
        element={
          <ProtectedRoute>
            <Campaigns />
          </ProtectedRoute>
        }
      />

      <Route
        path="/campaigns/new-campaign"
        element={
          <ProtectedRoute>
            <NewCampaing />
          </ProtectedRoute>
        }
      />

      <Route
        path="/campaigns/summary/:type/:id"
        element={
          <ProtectedRoute>
            <CampaignSummary />
          </ProtectedRoute>
        }
      />

      <Route
        path="/campaigns/edit-campaign/:id"
        element={
          <ProtectedRoute>
            <EditCampaing />
          </ProtectedRoute>
        }
      />

      <Route
        path="/campaigns/view-report/:id"
        element={
          <ProtectedRoute>
            <ViewCampaignReports />
          </ProtectedRoute>
        }
      />

      <Route
        path="/bulk-email/view-report/:id"
        element={
          <ProtectedRoute>
            <ViewCampaignReports />
          </ProtectedRoute>
        }
      />

      <Route
        path="/trigger"
        element={
          <ProtectedRoute>
            <Trigger />
          </ProtectedRoute>
        }
      />
      <Route
        path="/trigger/new-trigger"
        element={
          <ProtectedRoute>
            <AddTriggerForm />
          </ProtectedRoute>
        }
      />
       <Route
        path="/trigger/edit-trigger/:id"
        element={
          <ProtectedRoute>
            <EditTriggerForm />
          </ProtectedRoute>
        }
      />

      <Route
        path="/trigger/view-report/:id"
        element={
          <ProtectedRoute>
            <ViewCampaignReports />
          </ProtectedRoute>
        }
      />

      <Route
        path="/email-api/manage-clients"
        element={
          <ProtectedRoute>
            <EmailAPIClients />
          </ProtectedRoute>
        }
      />

      <Route
        path="/email-api/manage-clients/add-client"
        element={
          <ProtectedRoute>
            <AddEmailAPIClient />
          </ProtectedRoute>
        }
      />

      <Route
        path="/email-api/manage-clients/edit/:id"
        element={
          <ProtectedRoute>
            <EditEmailAPIClient />
          </ProtectedRoute>
        }
      />

      <Route
        path="/email-api"
        element={
          <ProtectedRoute>
            <ESPSettings />
          </ProtectedRoute>
        }
      />

      <Route
        path="/email-api/esp-settings/add-espsettings"
        element={
          <ProtectedRoute>
            <AddESPSetting />
          </ProtectedRoute>
        }
      />

      <Route
        path="/email-api/esp-settings/edit/:id"
        element={
          <ProtectedRoute>
            <EditESPAPI />
          </ProtectedRoute>
        }
      />
     <Route
        path="/reports/billing-report"
        element={
          <ProtectedRoute>
            <BillingReport />
          </ProtectedRoute>
        }
      />
       <Route
        path="/reports/testemailsend-report"
        element={
          <ProtectedRoute>
            <TestEmailsendReport />
            </ProtectedRoute>
        }
      />
      <Route
        path="/reports/bounce-report"
        element={
          <ProtectedRoute>
            <BounceReport />
          </ProtectedRoute>
        }
      />
      <Route
        path="/seed-list"
        element={
          <ProtectedRoute>
            <SeedList />
          </ProtectedRoute>
        }
      />
      <Route
        path="/suppression-list"
        element={
          <ProtectedRoute>
            <SuppressionList />
          </ProtectedRoute>
        }
      />
           <Route
        path="/bulk-email"
        element={
          <ProtectedRoute>
            <BulkEmail />
          </ProtectedRoute>
        }
      />
       <Route
        path="/bulk-email/new-bulk-campaign"
        element={
          <ProtectedRoute>
            <NewBulkCampaign />
          </ProtectedRoute>
        }
      />
       <Route
        path="/bulk-email/edit-bulk-email/:id"
        element={
          <ProtectedRoute>
            <EditBulkEmailCampaign />
          </ProtectedRoute>
        }
      />
      <Route
        path="/reports/campaign-schedule"
        element={
          <ProtectedRoute>
            <GanttSchedulePage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/unsubscribe-list"
        element={
          <ProtectedRoute>
            <Unsubscribe />
          </ProtectedRoute>
        }
        />
      <Route path="*" element={<PageNotFound />} />
    </Routes>
  );
};

const App: React.FC = () => {
  return (
    <Router>
      <ThemeProvider theme={theme} >
        <AuthProvider>
          <AppRoutes />
        </AuthProvider>
      </ThemeProvider>
    </Router>
  );
};

export default App;
